/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {MeasurementResult} from '../types/types';

/**
 * @internal
 *
 * Provides a single Performance Measurement for a given operation, allowing for
 * starting, stopping the measurement, as well as determining the current state
 * of the measurement, such as if it is running, and the total duration.
 *
 * This API is primarily intended to be a package level instance, that is, used
 * internally by the underlying PerformanceMonitor instances.
 */
export default class PerformanceMeasurement {
  /**
   * Provides a reference to the start time of the measurement instance,
   * i.e. the time at which the measurement was started.
   */
  protected _startTime: number;

  /**
   * Provides a reference to the end time of the measurement instance,
   * i.e. the time at which the measurement was ended.
   */
  protected _endTime: number;

  /**
   * Provides a reference to the total time of the measurement instance.
   */
  protected _totalTime: number;

  /**
   * Provides a reference to the running state of the measurement instance.
   */
  protected _isRunning: boolean;

  /**
   * The `PerformanceMeasurement` constructor, by default, automatically starts
   * the measurement when instantiated. The default behavior can be overridden
   * via the `autoStart` argument if needed.
   *
   * @param autostart When `true`, the measurement will automatically start when
   *        instantiated (defaults to `true`).
   */
  constructor(autostart = true) {
    if (autostart) {
      this.start(autostart);
    }
  }

  /**
   * Provides a getter for accessing the start time of the `PerformanceMeasurement`,
   * i.e. the time at which the measurement was started.
   *
   * @returns The time at which the measurement started, otherwise, `undefined`.
   */
  get startTime(): number | undefined {
    return this._startTime;
  }

  /**
   * Provides a getter for accessing the end time of the `PerformanceMeasurement`,
   * i.e. the time at which the measurement was completed.
   *
   * @returns The time at which the measurement started, otherwise, `undefined`.
   */
  get endTime(): number | undefined {
    return this._endTime;
  }

  /**
   * Returns the total time measured for the `PerformanceMeasurement` instance.
   *
   * ```typescript
   *  // reference total time of measurement ...
   *  const {duration} = measurement;
   * ```
   *
   * @returns returns the total time of the measurement (in milliseconds)
   */
  get duration(): number {
    return this._totalTime;
  }

  /**
   * Begins measuring the `PerformanceMeasurement` instance if it is not already
   * running. The `isRestart` argument can be used to override the default behavior
   * and restart the measurement.
   *
   * ```typescript
   *  // begin the measurement if not already running ...
   *  measurement.start();
   *  ...
   *  // begin the measurement again (restart) even if it is currently running ...
   *  measurement.start(true);
   * ```
   *
   * @param isRestart when `true`, indicates that if the measurement is currently
   *        running, it will be restarted.
   */
  start(isRestart = false) {
    if (!this._isRunning || isRestart) {
      this._startTime = performance.now();
      this._isRunning = true;
    }
  }

  /**
   * Ends measuring the `PerformanceMeasurement` instance if it is currently running,
   * and returns the total time of the measurement (in milliseconds).
   *
   * ```typescript
   *  // stop the measurement that is currently running ...
   *  measurement.stop();
   * ```
   *
   * @returns returns the total time of the measurement (in milliseconds)
   */
  stop(): number {
    if (this._isRunning) {
      this._isRunning = false;
      this._endTime   = performance.now();
      this._totalTime = this._endTime - this._startTime;
    }
    return this._totalTime;
  }

  /**
   * Determines if the measurement is currently running, returning `true` if so,
   * otherwise, `false`.
   *
   * ```typescript
   *  // determine if the measurement is currently running ...
   *  if (measurement.isRunning()) {
   *    ...
   *  }
   * ```
   *
   * @returns returns `true` if the measurement is currently running otherwise,
   *          `false`.
   */
  isRunning(): boolean {
    return this._isRunning;
  }

  /**
   * Returns the results of the `Performance` measurement which includes the
   * duration only (by default). To have the `start` and `end` times returned
   * as well, pass the `durationOnly` argument to `false`.
   *
   * The `fixedTime` argument can be used to have the result values formatted
   * in milliseconds rather than the raw high-precision values.
   *
   * @param   fixedTime when `true` (default), returns the measurement values
   *          in fixed millisecond format, otherwise, when `false` returns the
   *          measurement values in their raw high precision format.
   * @param   durationOnly when `true` (default), only the `duration` property
   *          will be returned. Otherwise, when `false` the `start` and `end`
   *          times will be returned as well.
   * @returns a `MeasurementResult` containing the measurements start time,
   *          end time, and total duration.
   */
  getResults(fixedTime = true, durationOnly = true): MeasurementResult | Partial<MeasurementResult> {
    const start    = this._startTime;
    const end      = this._endTime;
    const duration = this.duration;
    const results  = {
      duration : fixedTime ? PerformanceMeasurement.getFixedTime(duration) : duration
    };
    return !durationOnly ? {
      start : fixedTime ? PerformanceMeasurement.getFixedTime(start) : start,
      end   : fixedTime ? PerformanceMeasurement.getFixedTime(end) : end,
      ...results
    } : results;
  }

  /**
   * Provides a utility which converts the total measurement time from a
   * `DOMHighResTimeStamp` (as is returned by `performance.now();`) to
   * milliseconds.
   *
   * @returns the total duration of the measurement in milliseconds.
   */
  static getFixedTime(time: number): number {
    if (!isNaN(time)) {
      return +(time).toFixed(2);
    }
    return NaN;
  }
}
